import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import type { AdminCommunication, CommunicationAudience } from "../shared/communications-types";
import { resolveCommunicationError } from "../shared/resolve-communication-error";
import { useUpdateCommunicationConfig } from "../catalog/use-communications";

interface ConfigSectionProps {
  readonly communication: AdminCommunication;
}

interface ConfigDraft {
  readonly description: string;
  readonly audience: CommunicationAudience;
  readonly isCritical: boolean;
  readonly emailBackup: boolean;
}

function toDraft(c: AdminCommunication): ConfigDraft {
  return {
    description: c.description ?? "",
    audience: c.audience,
    isCritical: c.isCritical,
    emailBackup: c.emailBackup,
  };
}

const CHECKBOX_CLASS = "h-4 w-4";

export function ConfigSection({ communication }: ConfigSectionProps) {
  const { t } = useTranslation();
  const update = useUpdateCommunicationConfig();

  const [draft, setDraft] = useState<ConfigDraft>(() => toDraft(communication));
  const [error, setError] = useState<string | null>(null);

  // Re-seed only when we switch to a DIFFERENT communication — not on every
  // refetch. A sibling channel toggle invalidates the list and refetches it,
  // producing a new `communication` reference; re-seeding on that would wipe
  // the operator's in-progress edits. Keyed on id so cross-navigation still
  // resets, while same-id refetches leave the draft (and dirty state) intact.
  const seededId = useRef<string | null>(null);
  useEffect(() => {
    if (seededId.current !== communication.id) {
      seededId.current = communication.id;
      setDraft(toDraft(communication));
      setError(null);
    }
  }, [communication]);

  const original = toDraft(communication);
  const trimmedDescription = draft.description.trim();
  const isDirty =
    trimmedDescription !== (communication.description ?? "") ||
    draft.audience !== original.audience ||
    draft.isCritical !== original.isCritical ||
    draft.emailBackup !== original.emailBackup;

  const handleSave = async () => {
    if (!isDirty || update.isPending) return;
    setError(null);
    try {
      await update.mutateAsync({
        id: communication.id,
        input: {
          description:
            trimmedDescription !== (communication.description ?? "")
              ? trimmedDescription === ""
                ? null
                : trimmedDescription
              : undefined,
          audience: draft.audience !== original.audience ? draft.audience : undefined,
          isCritical: draft.isCritical !== original.isCritical ? draft.isCritical : undefined,
          emailBackup: draft.emailBackup !== original.emailBackup ? draft.emailBackup : undefined,
        },
      });
    } catch (err) {
      setError(resolveCommunicationError(err, t));
    }
  };

  return (
    <section
      data-testid="communication-config-section"
      className="space-y-4 rounded-md border border-border bg-card p-4"
    >
      <h3 className="text-lg font-semibold">{t("communications.config.heading")}</h3>

      <div className="space-y-2">
        <Label htmlFor="comm-description">{t("communications.config.description")}</Label>
        <Input
          id="comm-description"
          data-testid="communication-description-input"
          value={draft.description}
          onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
          placeholder={t("communications.config.descriptionPlaceholder")}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="comm-audience">{t("communications.config.audience")}</Label>
        <select
          id="comm-audience"
          data-testid="communication-audience-select"
          value={draft.audience}
          onChange={(e) =>
            setDraft((d) => ({
              ...d,
              audience: e.target.value as CommunicationAudience,
            }))
          }
          className="block h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <option value="USER">{t("communications.audience.USER")}</option>
          <option value="ADMIN">{t("communications.audience.ADMIN")}</option>
        </select>
        <p className="text-xs text-muted-foreground">{t("communications.config.audienceHint")}</p>
      </div>

      <div className="space-y-3">
        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            data-testid="communication-critical-input"
            checked={draft.isCritical}
            onChange={(e) => setDraft((d) => ({ ...d, isCritical: e.target.checked }))}
            className={`${CHECKBOX_CLASS} mt-0.5`}
          />
          <span>
            <span className="text-sm font-medium">{t("communications.config.isCritical")}</span>
            <span className="block text-xs text-muted-foreground">
              {t("communications.config.isCriticalHint")}
            </span>
          </span>
        </label>

        <label className="flex items-start gap-2">
          <input
            type="checkbox"
            data-testid="communication-email-backup-input"
            checked={draft.emailBackup}
            onChange={(e) => setDraft((d) => ({ ...d, emailBackup: e.target.checked }))}
            className={`${CHECKBOX_CLASS} mt-0.5`}
          />
          <span>
            <span className="text-sm font-medium">{t("communications.config.emailBackup")}</span>
            <span className="block text-xs text-muted-foreground">
              {t("communications.config.emailBackupHint")}
            </span>
          </span>
        </label>
      </div>

      {error && (
        <p data-testid="communication-config-error" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <Button
        data-testid="communication-config-save"
        onClick={handleSave}
        disabled={!isDirty || update.isPending}
      >
        {update.isPending ? t("communications.actions.saving") : t("communications.actions.save")}
      </Button>
    </section>
  );
}
